import React from "react";
import { KundliPredictionOrder } from "../../types/interfaces";

type ModalProps = {
  order: KundliPredictionOrder;
  handleModalOpenClose: (show: boolean) => void;
};

const KundliOrderDetail: React.FC<ModalProps> = ({ order, handleModalOpenClose }) => {
  const handleClose = () => {
    handleModalOpenClose(false);
  };

  const getPaymentBadge = () => {
    switch (order.payment_status) {
      case "Paid":
      case "Upfront":
        return "badge-light-success";
      case "Pending":
        return "badge-light-warning";
      case "Failed":
        return "badge-light-danger";
      default:
        return "badge-light-secondary";
    }
  };

  const getStatusBadge = () => {
    switch (order.status) {
      case "Completed":
        return "badge-light-success";
      case "Processing":
        return "badge-light-primary";
      case "Pending":
        return "badge-light-warning";
      case "Cancelled":
        return "badge-light-danger";
      default:
        return "badge-light-secondary";
    }
  };

  return (
    <>
      <div
        className="modal fade show"
        tabIndex={-1}
        aria-modal="true"
        role="dialog"
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable animate__animated animate__fadeInDown">
          <div className="modal-content">

            {/* ===== Header ===== */}
            <div className="modal-header border-0 pb-0">
              <h2 className="fw-bold">Kundli and Prediction Order Details</h2>
              <button
                type="button"
                className="btn btn-icon btn-sm btn-active-light-primary"
                onClick={handleClose}
              >
                <i className="ki-outline ki-cross fs-1" />
              </button>
            </div>

            {/* ===== Body ===== */}
            <div className="modal-body pt-4">
              <div className="row g-6 align-items-stretch">

                {/* ===== Order Info ===== */}
                <div className="col-lg-6 d-flex">
                  <div className="card card-flush shadow-sm h-100 w-100">
                    <div
                      className="card-header d-flex align-items-center justify-content-between cursor-pointer"
                      data-bs-toggle="collapse"
                      data-bs-target="#order_info"
                    >
                      <div className="d-flex align-items-center gap-2">
                        <i className="ki-outline ki-information-5 fs-2 text-primary" />
                        <h3 className="card-title fw-bold mb-0">Order Info</h3>
                      </div>
                      <span className="btn btn-icon btn-sm btn-active-light-primary">
                        <i className="ki-outline ki-down fs-3 rotate-180 transition" />
                      </span>
                    </div>

                    <div id="order_info" className="collapse show">
                      <div className="card-body fs-6">
                        <div className="d-flex justify-content-between mb-3">
                          <span className="text-muted">Order No</span>
                          <span className="fw-semibold">{order.order_number}</span>
                        </div>
                        <div className="d-flex justify-content-between mb-3">
                          <span className="text-muted">Booked On</span>
                          <span className="fw-semibold">
                            {new Date(order.created_at).toLocaleString()}
                          </span>
                        </div>
						<div className="d-flex justify-content-between mb-3">
                          <span className="text-muted">Booked By</span>
                          <span className="fw-semibold">
                            {typeof order.created_by === "object"
                            ? order.created_by.fullname
                            : "N/A"}
                          </span>
                        </div>
						<div className="d-flex justify-content-between mb-3">
                          <span className="text-muted">Booked For</span>
                          <span className="fw-semibold">
                            {order.request_for}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ===== Payment Info ===== */}
                <div className="col-lg-6 d-flex">
                  <div className="card card-flush shadow-sm h-100 w-100">
                    <div
                      className="card-header d-flex align-items-center justify-content-between cursor-pointer"
                      data-bs-toggle="collapse"
                      data-bs-target="#payment_info"
                    >
                      <div className="d-flex align-items-center gap-2">
                        <i className="ki-outline ki-wallet fs-2 text-success" />
                        <h3 className="card-title fw-bold mb-0">Payment Info</h3>
                      </div>
                      <span className="btn btn-icon btn-sm btn-active-light-primary">
                        <i className="ki-outline ki-down fs-3 rotate-180 transition" />
                      </span>
                    </div>

                    <div id="payment_info" className="collapse show">
                      <div className="card-body fs-6">
                        <div className="d-flex justify-content-between mb-3">
                          <span className="text-muted">Total Amount</span>
                          <span className="fw-semibold">
                            Rs. {order.total_amount || 0}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between mb-3">
                          <span className="text-muted">Paid Amount</span>
                          <span className="fw-semibold">
                            Rs.{" "}
                            {order.payment_status === "Upfront"
                              ? order.upfront_amount
                              : order.total_amount}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between mb-3">
                          <span className="text-muted">Payment Status</span>
                          <span className={`badge ${getPaymentBadge()}`}>
                            {order.payment_status}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Order Status</span>
                          <span className={`badge ${getStatusBadge()}`}>
                            {order.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ===== User Info ===== */}
                <div className="col-lg-6 d-flex">
                  <div className="card card-flush shadow-sm h-100 w-100">
                    <div
                      className="card-header d-flex align-items-center justify-content-between cursor-pointer"
                      data-bs-toggle="collapse"
                      data-bs-target="#user_info"
                    >
                      <div className="d-flex align-items-center gap-2">
                        <i className="ki-outline ki-profile-circle fs-2 text-info" />
                        <h3 className="card-title fw-bold mb-0">User Info</h3>
                      </div>
                      <span className="btn btn-icon btn-sm btn-active-light-primary">
                        <i className="ki-outline ki-down fs-3 rotate-180 transition" />
                      </span>
                    </div>

                    <div id="user_info" className="collapse show">
                      <div className="card-body fs-6">
                        
						<div className="d-flex justify-content-between mb-3">
                          <span className="text-muted">User Name</span>
                          <span className="fw-semibold">
                            {order.name}
                          </span>
                        </div>
						<div className="d-flex justify-content-between mb-3">
                          <span className="text-muted">User Email</span>
                          <span className="fw-semibold">
                            {order.email || "N/A"}
                          </span>
                        </div>
						<div className="d-flex justify-content-between mb-3">
                          <span className="text-muted">User Phone No</span>
                          <span className="fw-semibold">
                            {order.phone_no}
                          </span>
                        </div>
						<div className="d-flex justify-content-between mb-3">
                          <span className="text-muted">Date Of Birth</span>
                          <span className="fw-semibold">
                            {new Date(order.dob).toLocaleString()}
                          </span>
                        </div>
						<div className="d-flex justify-content-between mb-3">
                          <span className="text-muted">Time Of Birth</span>
                          <span className="fw-semibold">
                            {order.tob}
                          </span>
                        </div>
						<div className="d-flex justify-content-between mb-3">
                          <span className="text-muted">City</span>
                          <span className="fw-semibold">
                            {order.city}
                          </span>
                        </div>
						<div className="d-flex justify-content-between mb-3">
                          <span className="text-muted">State</span>
                          <span className="fw-semibold">
                            {order.state}
                          </span>
                        </div>
						<div className="d-flex justify-content-between mb-3">
                          <span className="text-muted">Country</span>
                          <span className="fw-semibold">
                            {order.country}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ===== Pandit Info ===== */}
                <div className="col-lg-6 d-flex">
                  <div className="card card-flush shadow-sm h-100 w-100">
                    <div
                      className="card-header d-flex align-items-center justify-content-between cursor-pointer"
                      data-bs-toggle="collapse"
                      data-bs-target="#pandit_info"
                    >
                      <div className="d-flex align-items-center gap-2">
                        <i className="ki-outline ki-user fs-2 text-warning" />
                        <h3 className="card-title fw-bold mb-0">Pandit Info</h3>
                      </div>
                      <span className="btn btn-icon btn-sm btn-active-light-primary">
                        <i className="ki-outline ki-down fs-3 rotate-180 transition" />
                      </span>
                    </div>

                    <div id="pandit_info" className="collapse show">
                      <div className="card-body d-flex align-items-center gap-4">
                        {order.pandit_id ? (
                          <>
                            <div className="symbol symbol-60px symbol-circle shadow-sm">
                              <img src={order.pandit_id.imagepath} alt="pandit-profile" />
                            </div>
                            <div>
                              <div className="fw-bold fs-5">
                                {order.pandit_id.fullname}
                              </div>
                              <div className="text-muted">Assigned Pandit</div>
                            </div>
                          </>
                        ) : (
                          <div className="text-muted">Not Assigned Yet</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ===== Additional Info ===== */}
                <div className="col-12">
                  <div className="card card-flush shadow-sm">
                    <div
                      className="card-header d-flex align-items-center justify-content-between cursor-pointer"
                      data-bs-toggle="collapse"
                      data-bs-target="#additional_info"
                    >
                      <div className="d-flex align-items-center gap-2">
                        <i className="ki-outline ki-notepad fs-2 text-dark" />
                        <h3 className="card-title fw-bold mb-0">Additional Info</h3>
                      </div>
                      <span className="btn btn-icon btn-sm btn-active-light-primary">
                        <i className="ki-outline ki-down fs-3 rotate-180 transition" />
                      </span>
                    </div>

                    <div id="additional_info" className="collapse show">
                      <div className="card-body fs-6">
                        <div className="mb-4">
                          <div className="fw-semibold mb-1">User Message</div>
                          <div className="text-muted">{order.message || "—"}</div>
                        </div>
                        <div>
                          <div className="fw-semibold mb-1">Notes</div>
                          <div className="text-muted">{order.notes || "—"}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="modal-backdrop fade show" />
    </>
  );
};

export default KundliOrderDetail;
